import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BannerCategory } from '../../data-access/models/banner-category';
import { Banner } from '../../data-access/models/banner.interface';
import { ApiImageModule } from '@digipay/ng-ui-api-image';
import { Action, ActionHandlerService, ActionType, RedirectAction } from '@client-monorepo/common/action-handler';
import { Slide } from '../../data-access/models/slide.interface';
import { SlideData } from '../../data-access/models/slide-data';
import { PromotionCarouselComponent } from '@client-monorepo/common/promotions';
import { SectionTypeBannerComponent } from '../section-type-banner/section-type-banner.component';
import { NgxCarouselComponent, NgxCarouselSlideDirective } from '@digipay/ngx-carousel';
import { CategoryStoresComponent } from '@client-monorepo/daily-fintech/stores';
import { AllStoresSectionComponent } from '@client-monorepo/daily-fintech/stores';
import { HorizontalScrollComponent, TitleSummaryComponent } from '@client-monorepo/common/ui-components';

@Component({
  selector: 'common-app-banners-shared-common-banners',
  standalone: true,
  imports: [
    CommonModule,
    ApiImageModule,
    PromotionCarouselComponent,
    SectionTypeBannerComponent,
    NgxCarouselComponent,
    NgxCarouselSlideDirective,
    CategoryStoresComponent,
    AllStoresSectionComponent,
    HorizontalScrollComponent,
    TitleSummaryComponent,
  ],
  templateUrl: './shared-common-banners.component.html',
  styleUrl: './shared-common-banners.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.d-none]': 'hideBanners()' },
})
export class SharedCommonBannersComponent {
  // Injections
  actionHandler = inject(ActionHandlerService);

  // Inputs
  bannersData = input.required<Banner[]>();
  categoryToShow = input.required<BannerCategory>();
  skeletonHeight = input<number>(186);
  isCarouselAnimationRunning = signal<boolean>(false);
  carouselIndex = signal<number>(0);
  singleBannerClasses = signal<string>('px-plus');
  carouselGap = input<number>(40);
  nothingToShow = signal<{ [key: number]: boolean }>({});
  hideBanners = computed(() => {
    if (!this.bannersToShow().length) {
      return true;
    }
    return Object.values(this.nothingToShow()).filter((item) => item).length === this.bannersToShow().length;
  });

  // Variables
  bannersToShow = computed<Banner[]>(() => this.decideForDisplay());

  decideForDisplay(): Banner[] {
    if (!(this.bannersData().length > 0)) {
      return [];
    }
    const isPresent = this.bannersData().find((banner) => banner.categories.includes(this.categoryToShow()));
    if (isPresent) {
      return this.generateBannersToShow();
    } else {
      return [];
    }
  }

  handleSlideClick(action: Action | undefined, slide: Slide): void {
    if (!action || this.isCarouselAnimationRunning()) {
      return;
    }
    if (action.type === ActionType.REDIRECT) {
      action = action as RedirectAction;
      action = {
        ...action,
        payload: {
          ...action.payload,
          params: {
            ...(action.payload.params ?? {}),
            'dp-source': 'DP',
            'dp-medium': 'banner',
            'dp-campaign': slide.extractedData.altText ?? '',
          },
        },
      } as RedirectAction;
    }

    this.actionHandler.handle(action);
  }

  handleCarouselIndexChange(index: number): void {
    this.carouselIndex.set(index);
  }

  handleAnimationRunning(event: boolean): void {
    this.isCarouselAnimationRunning.set(event);
  }

  generateBannersToShow(): Banner[] {
    return this.bannersData()
      .filter((banner) => banner.categories.includes(this.categoryToShow()))
      .sort((a, b) => a.order - b.order)
      .map((item) => {
        return {
          ...item,
          extractedConfig: item.config ? JSON.parse(item.config) : undefined,
          slides: this.generateSlidesExtractedData(item.slides),
        };
      });
  }

  generateSlidesExtractedData(slides: Slide[]): Slide[] {
    return slides
      .sort((a, b) => a.order - b.order)
      .map((slide) => {
        return {
          ...slide,
          extractedData: JSON.parse(slide.data) as SlideData,
          extractedAction: JSON.parse(slide.action) as Action,
        };
      });
  }

  getSlideDataItem(slide: Slide, key: string): string | undefined {
    const data = slide.data ? JSON.parse(slide.data) : [];
    return data[key] ?? undefined;
  }

  handleNothingToShow(mode: boolean, index: number) {
    this.nothingToShow.update((prev) => {
      return { ...prev, [index]: mode };
    });
  }
}
